// Libs
import { pipe } from 'fp-ts/function';
import { curry, filter, partition, some } from 'lodash';
import moment from 'moment';

// Utils

import {
  buildUnassignedRoleTerms,
  groupDOsByRole,
  sortRolesByRequired,
} from '@use-cases/districts/helpers';

import { getFormattedRYRange, isFutureRY } from '@utils/datetime';

import {
  DISTRICT_OFFICER_GOVERNOR_ROLES,
  DISTRICT_OFFICER_ROLES_TO_OMIT,
  DORole,
  DORoles,
  OfficerAssignedToRole,
  Role,
  RoleParams,
  ROLES_REQUIRED_BY_POLICY,
  UnassignedDistrictOfficerRoleTerm as Unassigned,
} from '@domui-domain/districts';
import { DynamicData } from 'src/domui/domain/type';

import { DistrictOfficer as DO } from '@typings/graphql';

const getAssignableTerm = (term: DO | Unassigned): boolean | undefined =>
  (term as Unassigned).isAssignable;

const getRoleWithAssignableTerm = (role: DORole): boolean =>
  some(role.terms, getAssignableTerm);

const filterUnassignedRoles = (result: DORoles): DORole[] =>
  filter(result, getRoleWithAssignableTerm);

const filterRoles = (roles: Role[]): Role[] =>
  roles.filter(
    ({ value, detail: { IsOfficer } }) =>
      IsOfficer && !DISTRICT_OFFICER_ROLES_TO_OMIT.includes(value)
  );

const filterDOsByRole = (officers: DynamicData, RY: number = 0) => {
  return officers?.flatMap((officer: { districtLeadership: [] }) =>
    officer.districtLeadership?.filter(
      (item: { isDelegated: boolean; role: string; endDate: string }) => {
        const { isDelegated, role, endDate } = item;

        return (
          !isDelegated &&
          !DISTRICT_OFFICER_ROLES_TO_OMIT.includes(role) &&
          (isFutureRY(RY)
            ? moment(endDate).isSameOrAfter(
                moment(getFormattedRYRange(RY).startYear),
                'D'
              )
            : true)
        );
      }
    )
  );
};
const sortRoles = curry(sortRolesByRequired)(ROLES_REQUIRED_BY_POLICY);

const splitRoles = (roles: DORoles): DORoles[] =>
  partition(roles, ({ role }) =>
    DISTRICT_OFFICER_GOVERNOR_ROLES.includes(role)
  );

const filterUnfilledDGRoles = (roles: DORoles): DORoles =>
  roles.filter(({ terms }) => !!terms.length);

const buildUnassignedRoleTermsForCAs = (
  year: number,
  roles: DORoles
): DORoles => roles.map(curry(buildUnassignedRoleTerms)(year));

const processRolesForManager = (year: number, roles: DORoles): DORoles => {
  const [governors, nonGovernors] = splitRoles(roles);

  return [
    ...filterUnfilledDGRoles(governors),
    ...buildUnassignedRoleTermsForCAs(year, nonGovernors),
  ];
};

const filterDORolesForViewer = (officers: DORoles) =>
  officers.filter(officer => officer.terms.length > 0);

const getDORolesForManager = ({ roles, officers, year }: RoleParams): DORoles =>
  pipe(
    groupDOsByRole(
      filterRoles(roles),
      filterDOsByRole(officers) as OfficerAssignedToRole[],
      Number(year)
    ),
    curry(processRolesForManager)(Number(year))
  );

const getDORolesForViewer = ({ roles, officers, year }: RoleParams): DORoles =>
  pipe(
    groupDOsByRole(
      filterRoles(roles),
      filterDOsByRole(officers, Number(year)) as OfficerAssignedToRole[],
      Number(year),
      true
    ),
    filterDORolesForViewer
  );

export const getDORoles = (isManager: boolean, params: RoleParams): DORoles[] =>
  pipe(
    (isManager ? getDORolesForManager : getDORolesForViewer)(params),
    sortRoles,
    splitRoles
  );

export const getDOUnassignedRoles = (params: RoleParams) =>
  pipe(
    getDORolesForManager(params),
    filterUnassignedRoles,
    sortRoles,
    splitRoles
  );
